import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, LogOut, LayoutDashboard, Calendar, Users, Wrench, 
  Image, FileText, TrendingUp, Clock, CheckCircle, AlertCircle,
  Plus, Edit, Trash2, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Check authentication
  useEffect(() => {
    const isAdmin = localStorage.getItem('cellrox_admin');
    if (!isAdmin) {
      navigate('/admin');
    }
  }, [navigate]);

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Always fetch stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);

      switch (activeTab) {
        case 'bookings':
          const bookingsRes = await axios.get(`${API}/bookings`);
          setBookings(bookingsRes.data);
          break;
        case 'leads':
          const leadsRes = await axios.get(`${API}/leads`);
          setLeads(leadsRes.data);
          break;
        case 'services':
          const servicesRes = await axios.get(`${API}/admin/services`);
          setServices(servicesRes.data);
          break;
        case 'gallery':
          const galleryRes = await axios.get(`${API}/admin/gallery`);
          setGallery(galleryRes.data);
          break;
        case 'blog':
          const blogRes = await axios.get(`${API}/admin/blog`);
          setBlogPosts(blogRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cellrox_admin');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const seedData = async () => {
    try {
      const response = await axios.post(`${API}/admin/seed`);
      toast.success(`Seeded ${response.data.services_added} services, ${response.data.gallery_added} gallery items`);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  // Booking actions
  const updateBookingStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status });
      toast.success('Booking status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`);
      toast.success('Booking deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  // Lead actions
  const updateLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status });
      toast.success('Lead status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  // Service actions
  const saveService = async (data) => {
    try {
      if (editingItem) {
        await axios.patch(`${API}/admin/services/${editingItem.id}`, data);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/admin/services`, data);
        toast.success('Service created');
      }
      setShowForm(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/admin/services/${id}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const toggleServiceActive = async (service) => {
    try {
      await axios.patch(`${API}/admin/services/${service.id}`, { is_active: !service.is_active });
      toast.success(service.is_active ? 'Service hidden' : 'Service visible');
      fetchData();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  // Gallery actions
  const saveGalleryItem = async (data) => {
    try {
      if (editingItem) {
        await axios.patch(`${API}/admin/gallery/${editingItem.id}`, data);
        toast.success('Gallery item updated');
      } else {
        await axios.post(`${API}/admin/gallery`, data);
        toast.success('Gallery item created');
      }
      setShowForm(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save gallery item');
    }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`);
      toast.success('Gallery item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete gallery item');
    }
  };

  // Blog actions
  const saveBlogPost = async (data) => {
    try {
      if (editingItem) {
        await axios.patch(`${API}/admin/blog/${editingItem.id}`, data);
        toast.success('Blog post updated');
      } else {
        await axios.post(`${API}/admin/blog`, data);
        toast.success('Blog post created');
      }
      setShowForm(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const deleteBlogPost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${id}`);
      toast.success('Blog post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'services', label: 'Services', icon: Wrench },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'blog', label: 'Blog', icon: FileText },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      qualified: 'bg-purple-100 text-purple-700',
      converted: 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">cellrox Admin</h1>
                <p className="text-xs text-cyan-100">Manage your business</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <a href="/" target="_blank" className="text-sm text-cyan-100 hover:text-white">
                View Website →
              </a>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <Card className="border-0 shadow-lg sticky top-6">
              <CardContent className="p-4">
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => { setActiveTab(tab.id); setShowForm(false); setEditingItem(null); }}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                        activeTab === tab.id
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <tab.icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  ))}
                </nav>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={seedData}
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Seed Initial Data
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              </div>
            ) : (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Dashboard Overview</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                      <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-slate-600">Total Bookings</p>
                              <p className="text-3xl font-bold text-slate-900">{stats.total_bookings}</p>
                              <p className="text-xs text-yellow-600 mt-1">{stats.pending_bookings} pending</p>
                            </div>
                            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-cyan-600" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-slate-600">Total Leads</p>
                              <p className="text-3xl font-bold text-slate-900">{stats.total_leads}</p>
                              <p className="text-xs text-green-600 mt-1">{stats.converted_leads} converted</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                              <Users className="w-6 h-6 text-blue-600" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-slate-600">Services</p>
                              <p className="text-3xl font-bold text-slate-900">{stats.total_services}</p>
                              <p className="text-xs text-slate-500 mt-1">Active on website</p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                              <Wrench className="w-6 h-6 text-purple-600" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-slate-600">Gallery Items</p>
                              <p className="text-3xl font-bold text-slate-900">{stats.total_gallery_items}</p>
                            </div>
                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                              <Image className="w-6 h-6 text-orange-600" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-0 shadow-lg">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-slate-600">Blog Posts</p>
                              <p className="text-3xl font-bold text-slate-900">{stats.total_blog_posts}</p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                              <FileText className="w-6 h-6 text-green-600" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Bookings Tab */}
                {activeTab === 'bookings' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-slate-900">Bookings ({bookings.length})</h2>
                    </div>
                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-0">
                        {bookings.length === 0 ? (
                          <div className="text-center py-12 text-slate-500">No bookings yet</div>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {bookings.map((booking) => (
                              <div key={booking.id} className="p-4 hover:bg-slate-50">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="font-semibold text-slate-900">{booking.name}</h3>
                                      <Badge className={getStatusColor(booking.status)}>{booking.status}</Badge>
                                    </div>
                                    <p className="text-sm text-slate-600">{booking.service}</p>
                                    <p className="text-sm text-slate-500">{booking.email} • {booking.phone}</p>
                                    {booking.message && <p className="text-sm text-slate-400 mt-1">"{booking.message}"</p>}
                                    <p className="text-xs text-slate-400 mt-2">{new Date(booking.created_at).toLocaleString()}</p>
                                  </div>
                                  <div className="flex space-x-2">
                                    <select
                                      value={booking.status}
                                      onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                                      className="text-sm border rounded-lg px-2 py-1"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="confirmed">Confirmed</option>
                                      <option value="completed">Completed</option>
                                      <option value="cancelled">Cancelled</option>
                                    </select>
                                    <Button variant="ghost" size="sm" onClick={() => deleteBooking(booking.id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Leads Tab */}
                {activeTab === 'leads' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-slate-900">Leads ({leads.length})</h2>
                      <a href="/leads" target="_blank">
                        <Button className="bg-gradient-to-r from-cyan-600 to-blue-600">
                          Open Lead Hunter →
                        </Button>
                      </a>
                    </div>
                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-0">
                        {leads.length === 0 ? (
                          <div className="text-center py-12 text-slate-500">No leads yet. Use Lead Hunter to scan for new leads.</div>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {leads.map((lead) => (
                              <div key={lead.id} className="p-4 hover:bg-slate-50">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="font-semibold text-slate-900">{lead.name || 'Anonymous'}</h3>
                                      <Badge className={getStatusColor(lead.status)}>{lead.status}</Badge>
                                    </div>
                                    <p className="text-sm text-slate-600 italic">"{lead.query}"</p>
                                    <p className="text-sm text-slate-500">{lead.location} • {lead.distance_km}km away</p>
                                    {lead.phone && <p className="text-sm text-cyan-600">{lead.phone}</p>}
                                  </div>
                                  <select
                                    value={lead.status}
                                    onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                                    className="text-sm border rounded-lg px-2 py-1"
                                  >
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="qualified">Qualified</option>
                                    <option value="converted">Converted</option>
                                    <option value="lost">Lost</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Services Tab */}
                {activeTab === 'services' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-slate-900">Services ({services.length})</h2>
                      <Button
                        onClick={() => { setShowForm(true); setEditingItem(null); }}
                        className="bg-gradient-to-r from-cyan-600 to-blue-600"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Service
                      </Button>
                    </div>
                    
                    {showForm && (
                      <ServiceForm
                        item={editingItem}
                        onSave={saveService}
                        onCancel={() => { setShowForm(false); setEditingItem(null); }}
                      />
                    )}

                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-0">
                        {services.length === 0 ? (
                          <div className="text-center py-12 text-slate-500">No services. Click "Seed Initial Data" to add default services.</div>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {services.map((service) => (
                              <div key={service.id} className={`p-4 hover:bg-slate-50 ${!service.is_active ? 'opacity-50' : ''}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-4">
                                    {service.image && (
                                      <img src={service.image} alt={service.name} className="w-16 h-16 object-cover rounded-lg" />
                                    )}
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <h3 className="font-semibold text-slate-900">{service.name}</h3>
                                        <Badge variant="outline">{service.category}</Badge>
                                        {!service.is_active && <Badge className="bg-red-100 text-red-700">Hidden</Badge>}
                                      </div>
                                      <p className="text-sm text-slate-600">{service.description}</p>
                                      <p className="text-sm text-slate-500">{service.time}</p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <Button variant="ghost" size="sm" onClick={() => toggleServiceActive(service)}>
                                      {service.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => { setEditingItem(service); setShowForm(true); }}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => deleteService(service.id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Gallery Tab */}
                {activeTab === 'gallery' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-slate-900">Gallery ({gallery.length})</h2>
                      <Button
                        onClick={() => { setShowForm(true); setEditingItem(null); }}
                        className="bg-gradient-to-r from-cyan-600 to-blue-600"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Gallery Item
                      </Button>
                    </div>
                    
                    {showForm && (
                      <GalleryForm
                        item={editingItem}
                        onSave={saveGalleryItem}
                        onCancel={() => { setShowForm(false); setEditingItem(null); }}
                      />
                    )}

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {gallery.length === 0 ? (
                        <div className="col-span-full text-center py-12 text-slate-500">No gallery items. Click "Seed Initial Data" to add defaults.</div>
                      ) : (
                        gallery.map((item) => (
                          <Card key={item.id} className={`border-0 shadow-lg ${!item.is_active ? 'opacity-50' : ''}`}>
                            <CardContent className="p-4">
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Before</p>
                                  <img src={item.before} alt="Before" className="w-full h-24 object-cover rounded-lg" />
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">After</p>
                                  <img src={item.after} alt="After" className="w-full h-24 object-cover rounded-lg" />
                                </div>
                              </div>
                              <p className="text-sm text-slate-700">{item.description}</p>
                              <div className="flex justify-end space-x-2 mt-3">
                                <Button variant="ghost" size="sm" onClick={() => { setEditingItem(item); setShowForm(true); }}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteGalleryItem(item.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Blog Tab */}
                {activeTab === 'blog' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-slate-900">Blog Posts ({blogPosts.length})</h2>
                      <Button
                        onClick={() => { setShowForm(true); setEditingItem(null); }}
                        className="bg-gradient-to-r from-cyan-600 to-blue-600"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Blog Post
                      </Button>
                    </div>
                    
                    {showForm && (
                      <BlogForm
                        item={editingItem}
                        onSave={saveBlogPost}
                        onCancel={() => { setShowForm(false); setEditingItem(null); }}
                      />
                    )}

                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-0">
                        {blogPosts.length === 0 ? (
                          <div className="text-center py-12 text-slate-500">No blog posts yet. Create your first SEO article!</div>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {blogPosts.map((post) => (
                              <div key={post.id} className={`p-4 hover:bg-slate-50 ${!post.is_published ? 'opacity-50' : ''}`}>
                                <div className="flex items-start justify-between">
                                  <div className="flex space-x-4">
                                    <img src={post.image} alt={post.title} className="w-24 h-16 object-cover rounded-lg" />
                                    <div>
                                      <div className="flex items-center space-x-2">
                                        <h3 className="font-semibold text-slate-900">{post.title}</h3>
                                        {!post.is_published && <Badge className="bg-yellow-100 text-yellow-700">Draft</Badge>}
                                      </div>
                                      <p className="text-sm text-slate-600">{post.excerpt.substring(0, 100)}...</p>
                                      <p className="text-xs text-slate-500 mt-1">{post.category} • {post.read_time} • {post.date}</p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer">
                                      <Button variant="ghost" size="sm">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </a>
                                    <Button variant="ghost" size="sm" onClick={() => { setEditingItem(post); setShowForm(true); }}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => deleteBlogPost(post.id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Service Form Component
const ServiceForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item || {
    category: '', name: '', description: '', icon: '', image: '', time: '', devices: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle>{item ? 'Edit Service' : 'Add New Service'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input placeholder="Service Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            <Input placeholder="Category (iPhone, MacBook, etc.)" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required />
          </div>
          <Textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
          <div className="grid md:grid-cols-3 gap-4">
            <Input placeholder="Icon (Smartphone, Laptop, etc.)" value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} required />
            <Input placeholder="Time (30-60 min)" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} required />
            <Input placeholder="Devices (optional)" value={formData.devices || ''} onChange={(e) => setFormData({...formData, devices: e.target.value})} />
          </div>
          <Input placeholder="Image URL" value={formData.image || ''} onChange={(e) => setFormData({...formData, image: e.target.value})} />
          <div className="flex space-x-3">
            <Button type="submit" className="bg-gradient-to-r from-cyan-600 to-blue-600">Save Service</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Gallery Form Component
const GalleryForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item || { before: '', after: '', description: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle>{item ? 'Edit Gallery Item' : 'Add Gallery Item'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input placeholder="Before Image URL" value={formData.before} onChange={(e) => setFormData({...formData, before: e.target.value})} required />
          <Input placeholder="After Image URL" value={formData.after} onChange={(e) => setFormData({...formData, after: e.target.value})} required />
          <Input placeholder="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
          <div className="flex space-x-3">
            <Button type="submit" className="bg-gradient-to-r from-cyan-600 to-blue-600">Save Gallery Item</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Blog Form Component
const BlogForm = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item || {
    slug: '', title: '', excerpt: '', image: '', category: '', author: 'cellrox Tech Team',
    read_time: '5 min read', seo_keywords: '', content: '', is_published: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <CardTitle>{item ? 'Edit Blog Post' : 'Create Blog Post'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input placeholder="Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
            <Input placeholder="Slug (url-friendly)" value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} required />
          </div>
          <Textarea placeholder="Excerpt (short description)" value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} required />
          <div className="grid md:grid-cols-3 gap-4">
            <Input placeholder="Category" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required />
            <Input placeholder="Read Time" value={formData.read_time} onChange={(e) => setFormData({...formData, read_time: e.target.value})} required />
            <Input placeholder="Author" value={formData.author} onChange={(e) => setFormData({...formData, author: e.target.value})} />
          </div>
          <Input placeholder="Image URL" value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} required />
          <Input placeholder="SEO Keywords (comma separated)" value={formData.seo_keywords} onChange={(e) => setFormData({...formData, seo_keywords: e.target.value})} required />
          <Textarea placeholder="Content (Markdown supported)" value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="min-h-[200px]" required />
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({...formData, is_published: e.target.checked})} className="rounded" />
              <span>Published</span>
            </label>
          </div>
          <div className="flex space-x-3">
            <Button type="submit" className="bg-gradient-to-r from-cyan-600 to-blue-600">Save Blog Post</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
